/**
 * Wallet Service
 * Manages agent wallet operations in Firestore
 * Wallet is stored at: Users/{auth UID}/wallet/{cash|checks}
 */
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  collectionGroup,
  getDocs,
  serverTimestamp,
  increment,
  arrayUnion,
  query,
  orderBy,
  where,
  runTransaction,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore'
import { getFirebaseDb } from '../firebase/firebase.js'

const WALLET_TYPES = ['cash', 'checks']

const WALLET_LABELS = {
  cash: 'מזומן',
  checks: 'שיקים',
}

/**
 * Fetch wallet data for a user
 * @param {string} uid - Firebase Auth UID
 * @returns {Object|null} Wallet data keyed by type, or null if no wallet
 */
async function getWallet(uid) {
  const db = getFirebaseDb()
  const walletRef = collection(db, 'Users', uid, 'wallet')
  const snapshot = await getDocs(walletRef)

  if (snapshot.empty) return null

  const wallet = {}
  snapshot.forEach((d) => {
    wallet[d.id] = { ...d.data(), id: d.id }
  })
  return wallet
}

/**
 * Create a new wallet with 2 buckets (cash, checks)
 * @param {string} uid - Firebase Auth UID
 * @param {Object} [options]
 * @param {string} [options.agentId] - Agent ID to associate with the wallet (used by admins)
 */
async function createWallet(uid, { agentId } = {}) {
  const db = getFirebaseDb()

  // Guard: never overwrite an existing wallet — a bare setDoc would zero the
  // bucket totals while leaving transactions intact (permanent desync)
  if (await walletExists(uid)) {
    throw new Error('Wallet already exists for this user')
  }

  const now = serverTimestamp()

  const promises = WALLET_TYPES.map((type) => {
    const docRef = doc(db, 'Users', uid, 'wallet', type)
    return setDoc(docRef, {
      totalAmount: 0,
      createdAt: now,
      updatedAt: now,
    })
  })

  // If agentId provided (admin wallet), store it on the User doc for easy querying
  if (agentId) {
    const userDocRef = doc(db, 'Users', uid)
    promises.push(setDoc(userDocRef, { walletAgentId: agentId }, { merge: true }))
  }

  await Promise.all(promises)
}

/**
 * Reset wallet to a true initial state (full wipe):
 * deletes ALL transactions (cash + checks), ALL discharge packages,
 * and zeroes both bucket totals. Irreversible — admin only (enforced by rules + UI).
 * @param {string} uid - Firebase Auth UID
 */
async function resetWallet(uid) {
  const db = getFirebaseDb()

  // Collect every doc that must be deleted (transactions in both buckets + packages)
  const refsToDelete = []
  for (const type of WALLET_TYPES) {
    const txSnap = await getDocs(collection(db, 'Users', uid, 'wallet', type, 'transactions'))
    txSnap.forEach((d) => refsToDelete.push(d.ref))
  }
  const pkgSnap = await getDocs(collection(db, 'Users', uid, 'walletPackages'))
  pkgSnap.forEach((d) => refsToDelete.push(d.ref))

  // Delete in chunks (Firestore batch limit is 500 ops)
  const BATCH_LIMIT = 500
  for (let i = 0; i < refsToDelete.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db)
    for (const ref of refsToDelete.slice(i, i + BATCH_LIMIT)) {
      batch.delete(ref)
    }
    await batch.commit()
  }

  // Zero both bucket totals
  const now = serverTimestamp()
  const zeroBatch = writeBatch(db)
  for (const type of WALLET_TYPES) {
    zeroBatch.set(
      doc(db, 'Users', uid, 'wallet', type),
      { totalAmount: 0, updatedAt: now },
      { merge: true }
    )
  }
  await zeroBatch.commit()
}

/**
 * Check if a wallet exists for a user
 * @param {string} uid - Firebase Auth UID
 * @returns {boolean}
 */
async function walletExists(uid) {
  const db = getFirebaseDb()
  const docRef = doc(db, 'Users', uid, 'wallet', 'cash')
  const snapshot = await getDoc(docRef)
  return snapshot.exists()
}

/**
 * Add a transaction to a wallet bucket and update the totalAmount
 * @param {string} uid - Firebase Auth UID
 * @param {string} type - Wallet type ('cash', 'credit', 'checks')
 * @param {Object} transactionData - Transaction details
 * @param {number} transactionData.amount - Payment amount
 * @param {string} transactionData.clientNum - Client number
 * @param {string} transactionData.clientName - Client name
 * @param {Array} transactionData.invoices - Array of invoice objects
 * @param {string} [transactionData.agentId] - Agent identifier
 * @returns {Promise<string>} Transaction document ID
 */
async function addWalletTransaction(uid, type, transactionData) {
  const db = getFirebaseDb()
  const now = serverTimestamp()

  // Add the transaction document to the subcollection
  const txRef = collection(db, 'Users', uid, 'wallet', type, 'transactions')
  const txDoc = await addDoc(txRef, {
    ...transactionData,
    createdAt: now,
  })

  // Update the parent wallet doc totalAmount
  const walletDocRef = doc(db, 'Users', uid, 'wallet', type)
  await setDoc(
    walletDocRef,
    { totalAmount: increment(transactionData.amount), updatedAt: now },
    { merge: true }
  )

  return txDoc.id
}

/**
 * Get all transactions for a wallet bucket
 * @param {string} uid - Firebase Auth UID
 * @param {string} type - Wallet type ('cash', 'credit', 'checks')
 * @returns {Promise<Array>} Array of transaction objects
 */
async function getWalletTransactions(uid, type) {
  const db = getFirebaseDb()
  const txRef = collection(db, 'Users', uid, 'wallet', type, 'transactions')
  const q = query(txRef, orderBy('createdAt', 'desc'))
  const snapshot = await getDocs(q)

  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
}

/**
 * Get next payment approval number (atomic increment)
 * Format: {currentYear}{counter padded to 6 digits}
 * Counter stored at Utils/Accounting.paymentApprovalNum
 * @returns {Promise<string>} Formatted approval number e.g. "2026000001"
 */
async function getNextPaymentApprovalNum() {
  const db = getFirebaseDb()
  const accountingRef = doc(db, 'Utils', 'Accounting')

  const nextNum = await runTransaction(db, async (transaction) => {
    const accountingDoc = await transaction.get(accountingRef)

    let currentNum = 0
    if (accountingDoc.exists()) {
      currentNum = accountingDoc.data().paymentApprovalNum || 0
    }

    const num = currentNum + 1
    transaction.update(accountingRef, { paymentApprovalNum: num })

    return num
  })

  const year = new Date().getFullYear()
  return `${year}${String(nextNum).padStart(6, '0')}`
}

/**
 * Get the wallet agent ID stored on the User doc (for admins who chose an agent ID)
 * @param {string} uid - Firebase Auth UID
 * @returns {Promise<string|null>} The stored walletAgentId or null
 */
async function getWalletAgentId(uid) {
  const db = getFirebaseDb()
  const userDocRef = doc(db, 'Users', uid)
  const snapshot = await getDoc(userDocRef)
  if (!snapshot.exists()) return null
  return snapshot.data().walletAgentId || null
}

/**
 * Subscribe to unified transactions (cash + checks merged by paymentApprovalNum)
 * Returns unsubscribe function. Callback receives merged transaction array.
 * @param {string} uid - Firebase Auth UID
 * @param {Function} callback - Called with array of unified transaction objects
 * @returns {Function} Unsubscribe function
 */
function subscribeUnifiedTransactions(uid, callback) {
  const db = getFirebaseDb()
  let cashTxs = []
  let checksTxs = []
  let cashReady = false
  let checksReady = false

  function merge() {
    if (!cashReady || !checksReady) return

    // Build a map keyed by paymentApprovalNum (or tx id for legacy entries)
    const map = new Map()

    for (const tx of cashTxs) {
      const key = tx.paymentApprovalNum || `__cash_${tx.id}`
      map.set(key, {
        unifiedKey: key,
        approvalNum: tx.paymentApprovalNum || '',
        createdAt: tx.createdAt,
        clientNum: tx.clientNum || '',
        clientName: tx.clientName || '',
        clientVat: tx.clientVat || '',
        cashAmount: tx.amount || 0,
        checksAmount: 0,
        checksCount: 0,
        checksDetails: [],
        invoices: tx.invoices || [],
        signatureUrl: tx.signatureUrl || '',
        contactEmails: tx.contactEmails || [],
        agentId: tx.agentId || '',
        isManual: tx.isManual || false,
        note: tx.note || '',
        cashTxId: tx.id,
        checksTxId: null,
      })
    }

    for (const tx of checksTxs) {
      const key = tx.paymentApprovalNum || `__checks_${tx.id}`
      if (map.has(key)) {
        const existing = map.get(key)
        existing.checksAmount = tx.amount || 0
        existing.checksCount = tx.checksDetails?.length || 0
        existing.checksDetails = tx.checksDetails || []
        existing.checksTxId = tx.id
        // Merge contact emails (union)
        if (tx.contactEmails?.length) {
          existing.contactEmails = [...new Set([...existing.contactEmails, ...tx.contactEmails])]
        }
        // Use signature from checks tx if cash didn't have one
        if (!existing.signatureUrl && tx.signatureUrl) {
          existing.signatureUrl = tx.signatureUrl
        }
        // Use note from checks tx if cash didn't have one
        if (!existing.note && tx.note) {
          existing.note = tx.note
        }
      } else {
        map.set(key, {
          unifiedKey: key,
          approvalNum: tx.paymentApprovalNum || '',
          createdAt: tx.createdAt,
          clientNum: tx.clientNum || '',
          clientName: tx.clientName || '',
          clientVat: tx.clientVat || '',
          cashAmount: 0,
          checksAmount: tx.amount || 0,
          checksCount: tx.checksDetails?.length || 0,
          checksDetails: tx.checksDetails || [],
          invoices: tx.invoices || [],
          signatureUrl: tx.signatureUrl || '',
          contactEmails: tx.contactEmails || [],
          agentId: tx.agentId || '',
          isManual: tx.isManual || false,
          note: tx.note || '',
          cashTxId: null,
          checksTxId: tx.id,
        })
      }
    }

    // Sort by date descending
    const result = Array.from(map.values()).sort((a, b) => {
      const aTime = a.createdAt?.seconds || 0
      const bTime = b.createdAt?.seconds || 0
      return bTime - aTime
    })

    callback(result)
  }

  const cashRef = collection(db, 'Users', uid, 'wallet', 'cash', 'transactions')
  const cashQ = query(cashRef, orderBy('createdAt', 'desc'))
  const unsubCash = onSnapshot(cashQ, (snap) => {
    cashTxs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    cashReady = true
    merge()
  })

  const checksRef = collection(db, 'Users', uid, 'wallet', 'checks', 'transactions')
  const checksQ = query(checksRef, orderBy('createdAt', 'desc'))
  const unsubChecks = onSnapshot(checksQ, (snap) => {
    checksTxs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    checksReady = true
    merge()
  })

  return () => {
    unsubCash()
    unsubChecks()
  }
}

/**
 * Generate a random 6-digit package ID
 * @returns {string} 6-digit numeric string
 */
function generatePackageId() {
  return String(Math.floor(100000 + Math.random() * 900000))
}

/**
 * Create a discharge package from selected wallet transactions.
 * Atomically: creates the package doc, deletes original transactions, and updates bucket totals.
 * @param {string} uid - Firebase Auth UID
 * @param {Object} packageData
 * @param {Array} packageData.items - Unified transaction objects (with cashTxId/checksTxId)
 * @param {string} packageData.agentName
 * @param {string} packageData.date
 * @param {string} packageData.time
 * @param {string} packageData.recipientName
 * @param {boolean} packageData.isOffice
 * @param {number} packageData.totalCash - Sum of cash amounts
 * @param {number} packageData.totalChecks - Count of checks
 * @returns {Promise<{packageId: string, totalCash: number, totalChecks: number, itemCount: number}>}
 */
async function createDischargePackage(uid, packageData) {
  const db = getFirebaseDb()
  const {
    items,
    agentName,
    date,
    time,
    recipientName,
    isOffice,
    recipientType,
    receiver,
    totalCash,
    totalChecks,
    signatureData,
    expenses,
    totalExpenses,
    netCash,
  } = packageData

  const hasExpenses = Array.isArray(expenses) && expenses.length > 0

  // Compute total checks amount (for subtracting from bucket)
  let totalChecksAmount = 0
  for (const item of items) {
    totalChecksAmount += item.checksAmount || 0
  }

  const result = await runTransaction(db, async (transaction) => {
    // Generate a unique 6-digit package ID
    let packageId
    let packageRef
    for (let attempt = 0; attempt < 10; attempt++) {
      packageId = generatePackageId()
      packageRef = doc(db, 'Users', uid, 'walletPackages', packageId)
      const existing = await transaction.get(packageRef)
      if (!existing.exists()) break
      if (attempt === 9) throw new Error('Failed to generate unique package ID')
    }

    // Read all transaction docs that need to be deleted (reads must come before writes)
    const txRefsToDelete = []
    for (const item of items) {
      if (item.cashTxId) {
        const cashRef = doc(db, 'Users', uid, 'wallet', 'cash', 'transactions', item.cashTxId)
        await transaction.get(cashRef)
        txRefsToDelete.push(cashRef)
      }
      if (item.checksTxId) {
        const checksRef = doc(db, 'Users', uid, 'wallet', 'checks', 'transactions', item.checksTxId)
        await transaction.get(checksRef)
        txRefsToDelete.push(checksRef)
      }
    }

    // Read bucket docs
    const cashBucketRef = doc(db, 'Users', uid, 'wallet', 'cash')
    const checksBucketRef = doc(db, 'Users', uid, 'wallet', 'checks')
    await transaction.get(cashBucketRef)
    await transaction.get(checksBucketRef)

    // --- All reads done, now writes ---

    // Custody: package starts in the creator's hands unless it was handed to
    // another system user (agent/admin) at discharge time. The original
    // creator (agentName) is immutable on the doc — the chain records hops.
    const nowIso = new Date().toISOString()
    const effectiveType = recipientType || (isOffice ? 'office' : 'driver')
    const handedToAgent = effectiveType === 'agent' && receiver?.uid
    const custodyChain = [
      { type: 'created', uid, name: agentName || '', at: nowIso },
      {
        type: effectiveType,
        uid: handedToAgent ? receiver.uid : '',
        name: handedToAgent ? receiver.name || recipientName : recipientName,
        at: nowIso,
      },
    ]

    // Create the package doc
    const packagePayload = {
      items,
      agentName,
      date,
      time,
      recipientName,
      isOffice: effectiveType === 'office',
      recipientType: effectiveType,
      holderUid: handedToAgent ? receiver.uid : uid,
      holderName: handedToAgent ? receiver.name || recipientName : agentName || '',
      custodyChain,
      signatureData: signatureData || '',
      status: 'שינוע',
      totalCash,
      totalChecks,
      createdAt: serverTimestamp(),
    }

    if (hasExpenses) {
      packagePayload.expenses = expenses
      packagePayload.totalExpenses = totalExpenses || 0
      packagePayload.netCash = typeof netCash === 'number' ? netCash : totalCash
    }

    transaction.set(packageRef, packagePayload)

    // Delete original transaction docs
    for (const ref of txRefsToDelete) {
      transaction.delete(ref)
    }

    // Decrement bucket totals
    if (totalCash > 0) {
      transaction.update(cashBucketRef, {
        totalAmount: increment(-totalCash),
        updatedAt: serverTimestamp(),
      })
    }
    if (totalChecksAmount > 0) {
      transaction.update(checksBucketRef, {
        totalAmount: increment(-totalChecksAmount),
        updatedAt: serverTimestamp(),
      })
    }

    return packageId
  })

  return {
    packageId: result,
    totalCash,
    totalChecks,
    itemCount: items.length,
    expenses: hasExpenses ? expenses : [],
    totalExpenses: hasExpenses ? totalExpenses || 0 : 0,
    netCash: hasExpenses ? (typeof netCash === 'number' ? netCash : totalCash) : totalCash,
  }
}

/**
 * Get count of packages with a specific status for a user
 * @param {string} uid - Firebase Auth UID
 * @param {string} status - Package status to filter by
 * @returns {Promise<number>} Count of matching packages
 */
async function getPackagesCountByStatus(uid, status) {
  const db = getFirebaseDb()
  const packagesRef = collection(db, 'Users', uid, 'walletPackages')
  const q = query(packagesRef, where('status', '==', status))
  const snapshot = await getDocs(q)
  return snapshot.size
}

/**
 * Get count of packages a user currently HOLDS with a specific status
 * (custody view — mirrors subscribeHeldWalletPackages): own packages that
 * were not handed onward + packages transferred to them.
 * @param {string} uid - Firebase Auth UID
 * @param {string} status - Package status to filter by
 * @returns {Promise<number>} Count of matching packages
 */
async function getHeldPackagesCountByStatus(uid, status) {
  const db = getFirebaseDb()
  const ownQ = query(collection(db, 'Users', uid, 'walletPackages'), where('status', '==', status))
  const heldQ = query(collectionGroup(db, 'walletPackages'), where('holderUid', '==', uid))
  const [ownSnap, heldSnap] = await Promise.all([getDocs(ownQ), getDocs(heldQ)])

  const paths = new Set()
  ownSnap.forEach((d) => {
    const data = d.data()
    // Handed onward — no longer in this user's custody
    if (data.holderUid && data.holderUid !== uid) return
    paths.add(d.ref.path)
  })
  heldSnap.forEach((d) => {
    if (d.data().status === status) paths.add(d.ref.path)
  })
  return paths.size
}

/**
 * Map a package snapshot doc to an enriched package object:
 * ownerUid (from path), clientCount (unique client numbers from items[])
 * @param {Object} d - Firestore document snapshot
 * @returns {Object} Enriched package object
 */
function mapPackageDoc(d) {
  const data = d.data()
  const items = data.items || []
  const uniqueClients = new Set(items.map((i) => i.clientNum).filter(Boolean))
  // Document path: Users/{uid}/walletPackages/{id}
  const ownerUid = d.ref.path.split('/')[1] || ''
  return {
    id: d.id,
    ...data,
    ownerUid,
    clientCount: uniqueClients.size,
  }
}

/**
 * Current holder of a package (custody). Legacy packages without holder
 * fields are held by their creator/owner.
 * @param {Object} pkg - Package object (with ownerUid)
 * @returns {{uid: string, name: string}}
 */
function getPackageHolder(pkg) {
  if (!pkg) return { uid: '', name: '' }
  return {
    uid: pkg.holderUid || pkg.ownerUid || '',
    name: pkg.holderName || pkg.agentName || '',
  }
}

/**
 * Whether the package was handed to another system user at least once
 * @param {Object} pkg - Package object
 * @returns {boolean}
 */
function isPackageTransferred(pkg) {
  return (pkg?.custodyChain || []).filter((e) => e.type === 'agent').length > 0
}

/**
 * Subscribe to wallet packages for a user (real-time)
 * Enriches each package with clientCount (unique client numbers from items[])
 * @param {string} uid - Firebase Auth UID
 * @param {Function} callback - Called with array of package objects
 * @returns {Function} Unsubscribe function
 */
function subscribeWalletPackages(uid, callback) {
  const db = getFirebaseDb()
  const packagesRef = collection(db, 'Users', uid, 'walletPackages')
  const q = query(packagesRef, orderBy('createdAt', 'desc'))

  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(mapPackageDoc))
  })
}

/**
 * Subscribe to the packages a user currently HOLDS (custody view for the
 * kupa screen): own packages that were not handed onward + packages
 * transferred to them from other users' subtrees.
 * @param {string} uid - Firebase Auth UID
 * @param {Function} callback - Called with array of package objects
 * @returns {Function} Unsubscribe function
 */
function subscribeHeldWalletPackages(uid, callback) {
  const db = getFirebaseDb()
  let ownPackages = []
  let heldPackages = []
  let ownReady = false
  let heldReady = false

  function emit() {
    if (!ownReady || !heldReady) return
    const map = new Map()
    // Own packages: legacy docs (no holderUid) are held by the owner;
    // docs handed onward (holderUid someone else) left this kupa
    for (const pkg of ownPackages) {
      if (pkg.holderUid && pkg.holderUid !== uid) continue
      map.set(`${pkg.ownerUid}_${pkg.id}`, pkg)
    }
    // Packages transferred to this user (may live under another owner)
    for (const pkg of heldPackages) {
      map.set(`${pkg.ownerUid}_${pkg.id}`, pkg)
    }
    const result = Array.from(map.values()).sort(
      (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
    )
    callback(result)
  }

  const ownRef = collection(db, 'Users', uid, 'walletPackages')
  const unsubOwn = onSnapshot(query(ownRef, orderBy('createdAt', 'desc')), (snap) => {
    ownPackages = snap.docs.map(mapPackageDoc)
    ownReady = true
    emit()
  })

  const heldQ = query(collectionGroup(db, 'walletPackages'), where('holderUid', '==', uid))
  const unsubHeld = onSnapshot(heldQ, (snap) => {
    heldPackages = snap.docs.map(mapPackageDoc)
    heldReady = true
    emit()
  })

  return () => {
    unsubOwn()
    unsubHeld()
  }
}

/**
 * Hand a package onward (custody transfer). Appends a custody-chain entry and
 * updates the holder/recipient fields. The package doc stays under its
 * original owner; amounts/items/creator are never touched.
 * Allowed for the current holder or an admin (enforced by Firestore rules).
 * @param {string} ownerUid - UID of the package owner (document parent)
 * @param {string} packageId - Package document ID
 * @param {Object} transfer
 * @param {'office'|'agent'|'driver'} transfer.type - Destination type
 * @param {string} transfer.name - Recipient display name
 * @param {string} [transfer.uid] - Receiving user UID (type 'agent' only)
 * @param {string} transfer.byUid - UID of the user handing the package
 * @param {string} transfer.byName - Name of the user handing the package
 * @param {string} [transfer.signatureData] - Base64 recipient signature
 */
async function transferWalletPackage(ownerUid, packageId, transfer) {
  const db = getFirebaseDb()
  const { type, name, uid, byUid, byName, signatureData } = transfer

  const toAgent = type === 'agent' && uid
  const entry = {
    type,
    uid: toAgent ? uid : '',
    name: name || '',
    byUid: byUid || '',
    byName: byName || '',
    at: new Date().toISOString(),
    signatureData: signatureData || '',
  }

  await updateDoc(doc(db, 'Users', ownerUid, 'walletPackages', packageId), {
    recipientType: type,
    recipientName: name || '',
    isOffice: type === 'office',
    // Driver/office handoffs keep custody responsibility with the transferor
    holderUid: toAgent ? uid : byUid,
    holderName: toAgent ? name || '' : byName || '',
    custodyChain: arrayUnion(entry),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Subscribe to ALL wallet packages across all users (collectionGroup query).
 * Admin-only. Enriches each package with ownerUid and clientCount.
 * @param {Function} callback - Called with array of package objects
 * @returns {Function} Unsubscribe function
 */
function subscribeAllWalletPackages(callback) {
  const db = getFirebaseDb()
  const q = query(collectionGroup(db, 'walletPackages'), orderBy('createdAt', 'desc'))

  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(mapPackageDoc))
  })
}

/**
 * Hard-delete a discharge package document.
 * No fund restoration — the wallet buckets were already decremented at discharge.
 * Admin-only per UI; Firestore rules allow admin + owner.
 * @param {string} ownerUid - UID of the wallet owner (package parent)
 * @param {string} packageId - Package document ID
 */
async function deleteWalletPackage(ownerUid, packageId) {
  const db = getFirebaseDb()
  await deleteDoc(doc(db, 'Users', ownerUid, 'walletPackages', packageId))
}

/**
 * Net cash of a discharge package: gross cash minus recorded expenses.
 * Falls back gracefully for legacy packages without expense fields
 * (no expenses → net === gross).
 * @param {Object} pkg - Package doc data
 * @returns {number}
 */
function getPackageNetCash(pkg) {
  if (!pkg) return 0
  if (typeof pkg.netCash === 'number') return pkg.netCash
  return (pkg.totalCash || 0) - (pkg.totalExpenses || 0)
}

/**
 * Batch-update status for multiple packages across different users.
 * @param {Array<{ownerUid: string, packageId: string}>} packageRefs
 * @param {string} newStatus - New status value (e.g. 'נמסר')
 * @param {string} [signatureData] - Optional base64 signature
 * @returns {Promise<void>}
 */
async function updatePackagesStatus(packageRefs, newStatus, signatureData = '') {
  const db = getFirebaseDb()
  const batch = writeBatch(db)

  const intakeEntry = {
    type: 'intake',
    name: 'משרדים',
    at: new Date().toISOString(),
  }

  for (const { ownerUid, packageId } of packageRefs) {
    const ref = doc(db, 'Users', ownerUid, 'walletPackages', packageId)
    const updateData = {
      status: newStatus,
      deliveredAt: serverTimestamp(),
      // Close the custody chain explicitly
      custodyChain: arrayUnion(intakeEntry),
    }
    if (signatureData) {
      updateData.officeSignatureData = signatureData
    }
    batch.update(ref, updateData)
  }

  await batch.commit()
}

export {
  getWallet,
  createWallet,
  resetWallet,
  walletExists,
  addWalletTransaction,
  getWalletTransactions,
  getNextPaymentApprovalNum,
  getWalletAgentId,
  subscribeUnifiedTransactions,
  createDischargePackage,
  getPackagesCountByStatus,
  getHeldPackagesCountByStatus,
  subscribeWalletPackages,
  subscribeHeldWalletPackages,
  subscribeAllWalletPackages,
  transferWalletPackage,
  updatePackagesStatus,
  deleteWalletPackage,
  getPackageNetCash,
  getPackageHolder,
  isPackageTransferred,
  WALLET_TYPES,
  WALLET_LABELS,
}
